<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="navigateToMainPage" />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{ $t('payment.surchargeTitle') }}
          </h2>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <FreeLoader v-if="surchargeData.isLoading.value" class="m-auto" />
      <div
        v-else-if="surchargeData.bill.value"
        class="mt-4 flex grow flex-col justify-between"
      >
        <div class="space-y-6">
          <PriceDetails :total-price="surchargeData.bill.value.amount" />
          <CountdownTimer
            v-if="surchargeData.expiresAt.value"
            :expires-at="surchargeData.expiresAt.value"
          >
            <p class="text-text-neutral-tertiary text-sm font-medium">
              {{
                $t('payment.timer.title', {
                  orderType: $t('payment.timer.surcharge'),
                })
              }}
            </p>
          </CountdownTimer>
        </div>
        <FreeButton
          type="button"
          class="green-main-button sticky bottom-4 py-4"
          @click="startPayment"
        >
          <i18n-t keypath="payment.paySurcharge" tag="p">
            <template #price>
              <span v-price="surchargeData.bill.value.amount"></span>
            </template>
          </i18n-t>
        </FreeButton>
      </div>
      <PaidPaymentErrorModal
        :name="paidErrorModal"
        is-surcharge
        @onProceed="proceedToOrder"
      />
      <ExpiredPaymentModal :name="expiredErrorModal" is-surcharge />
      <DefaultPaymentErrorModal
        :name="defaultErrorModal"
        is-surcharge
        @onRetry="router.go(0)"
      />
      <PaymentErrorModal
        :name="paymentErrorModal"
        @onProceed="hidePaymentErrorModal"
      />
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    FreeHeader,
    FreePageLayout,
    FreeBackButton,
    FreeLoader,
    FreeButton,
    CountdownTimer,
    appNavLinks,
    PaymentErrorModal,
    ExpiredPaymentModal,
    PaidPaymentErrorModal,
    DefaultPaymentErrorModal,
    freedomPayment,
    freedomApp,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';

  import { surcharge } from '@/features/use-surcharge';

  import { PriceDetails } from '@/entities/price-details';

  import { sentryModel } from '@/shared/model';
  import { api } from '@/shared/api';
  import { appConfig } from '@/shared/config';

  const props = defineProps<{ billId: string }>();

  const router = useRouter();

  const modal = controllerModel.useModal();
  const paidErrorModal = 'paid-surcharge-modal';
  const paymentErrorModal = 'payment-error-modal';
  const defaultErrorModal = 'default-error-modal';
  const expiredErrorModal = 'expired-surcharge-modal';
  const showPaidErrorModal = () => modal.show(paidErrorModal);
  const showDefaultErrorModal = () => modal.show(defaultErrorModal);
  const showExpiredErrorModal = () => modal.show(expiredErrorModal);
  const showPaymentErrorModal = () => modal.show(paymentErrorModal);
  const hidePaymentErrorModal = () => modal.hide(paymentErrorModal);

  const surchargeData = surcharge.model.useSurcharge();
  const hasError = computed(
    () =>
      !surchargeData.bill.value ||
      surchargeData.isExpired.value ||
      surchargeData.hasError.value,
  );

  const navigateToMainPage = () => router.replace({ name: 'MainPage' });
  const proceedToOrder = () => {
    if (surchargeData.bill.value?.order) {
      appNavLinks.openCabinet('orders', surchargeData.bill.value.order);
    } else {
      // Impossible, but we still need to throw error to make sure it is not neglected and logged
      throw new Error('ERR_SURCHARGE_NO_ORDER_ID');
    }
  };

  const startPayment = () => {
    const paymentArguments = {
      api,
      billId: props.billId,
      isDevRuntime: appConfig.isDevRuntimeEnv,
      prodPay: freedomApp.lib.avia.payByBillId,
    };
    freedomPayment.model
      .useFreedomPayment(paymentArguments)
      .pay.then(postPaymentAction => {
        if (postPaymentAction === 'goToMainPage') {
          navigateToMainPage();
        } else {
          proceedToOrder();
        }
      })
      .catch(error => {
        showPaymentErrorModal();
        sentryModel.captureException(
          sentryModel.getReadableError('ERR_FAILED_TO_PAY_SURCHARGE', error),
        );
      });
  };

  onMounted(() =>
    surchargeData.fetchBill(props.billId).finally(() => {
      if (hasError.value) {
        if (surchargeData.isExpired.value) {
          showExpiredErrorModal();
        } else if (surchargeData.isPaid.value) {
          showPaidErrorModal();
        } else {
          showDefaultErrorModal();
        }
      }
    }),
  );
</script>
